import { Component, ElementRef, HostBinding, Input, OnDestroy, OnInit, viewChild, HostListener, inject, output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UserPreferencesService } from '@core/services/user-preferences.service';
import { isEmbeddedView } from '@core/utils/functions';
import { MenuService } from './app.menu.service';
import { LayoutService } from './service/app.layout.service';
import { MenuItemDto } from './app.menu.component';

@Component({
  selector: '[app-menuitem]',
  standalone: false,
  template: `
    <ng-container>
      @if (root) {
        <div class="layout-menuitem-root-text">{{ item.label }}</div>
      }
      <!-- In the wide menu a group row does one thing: open or close its list. Its own page is listed first. -->
      <a
        (click)="itemClick($event, item)"
        [routerLink]="isGroupToggle ? null : item.routerLink"
        [queryParams]="linkQueryParams"
        routerLinkActive="active-route"
        [attr.role]="isGroupToggle ? 'button' : null"
        [attr.tabindex]="isGroupToggle ? 0 : null"
        [attr.aria-expanded]="isGroupToggle ? active : null"
        (keydown.enter)="isGroupToggle && itemClick($event, item)"
        (keydown.space)="isGroupToggle && itemClick($event, item); isGroupToggle && $event.preventDefault()"
        pRipple>
        <i [ngClass]="item.icon" class="layout-menuitem-icon" [pTooltip]="item.tooltip"></i>
        <span class="layout-menuitem-text flex items-center gap-2">
          {{ item.label }}
          @if (item.loading) {
            <div class="w-4 h-4 border border-primary-500 border-solid rounded-full border-t-transparent animate-spin"></div>
          }
        </span>
        @if (isAccordion && item.items?.length) {
          <i class="pi pi-chevron-down menu-chevron" [class.menu-chevron-open]="active" aria-hidden="true"></i>
        }
      </a>

      @if (item.items?.length) {
        <ul
          #submenu
          [class.submenu-collapsed]="isCollapsed()"
          [class.submenu-expanded]="isExpanded()"
          [class.submenu-hidden]="isHidden()"
          [class.submenu-visible]="isVisible()"
          class="ml-14 submenu-animation">
          <!-- The pop-out's title. Not in the wide menu's inline list: it would repeat the item right above it. -->
          @if (!isAccordion) {
            <li class="mb-2 pl-1.25 font-medium flex items-center gap-2">
              {{ item.label }}
              @if (item.loading) {
                <div class="w-4 h-4 border border-primary-500 border-solid rounded-full border-t-transparent animate-spin"></div>
              }
            </li>
          }
          @if (isAccordion && overviewItem) {
            <li app-menuitem [item]="overviewItem" [index]="item.items!.length" [parentKey]="key"></li>
          }
          @for (child of item.items; track $index; let i = $index) {
            <li app-menuitem [item]="child" [index]="i" [parentKey]="key"></li>
          }
        </ul>
      }
    </ng-container>
  `,
  styles: [
    `
      .submenu-animation {
        overflow: hidden;
        transition: height 400ms cubic-bezier(0.86, 0, 0.07, 1);
      }

      .submenu-collapsed {
        height: 0;
      }

      .submenu-expanded {
        height: auto;
      }

      .submenu-hidden {
        display: none;
      }

      .submenu-visible {
        display: block;
      }
    `
  ]
})
export class AppMenuitemComponent implements OnInit, OnDestroy {
  @Input() item!: MenuItemDto;
  @Input() index!: number;
  @Input() @HostBinding('class.layout-root-menuitem') root!: boolean;
  @Input() parentKey!: string;
  submenu = viewChild<ElementRef>('submenu');
  active = false;
  menuSourceSubscription: Subscription;
  menuResetSubscription: Subscription;
  key: string = '';
  private hoverTimeoutId: any;
  /** The group's own page as the first entry of its inline list (wide menu only). */
  overviewItem: MenuItemDto | null = null;
  /**
   * A menu link carries only its own query params. Merging the current URL's made every link inherit the
   * page's filters (on In Transit, every menu link read ?searchMode=intransit). The embedded-view flag is
   * the one param that must survive navigation.
   */
  linkQueryParams: Record<string, any> | null = null;

  mouseEnterItemEvent = output<MenuItemDto | null>();

  private readonly userPreferences = inject(UserPreferencesService);

  constructor(
    public layoutService: LayoutService,
    public router: Router,
    private menuService: MenuService
  ) {
    this.menuSourceSubscription = this.menuService.menuSource$.subscribe((value) => {
      Promise.resolve(null).then(() => {
        if (value.routeEvent) {
          const wasActive = this.active;
          this.active = value.key === this.key || value.key.startsWith(this.key + '-') ? true : false;
          // Only when the group OPENS: every navigation re-announces the current route, and announcing each time
          // re-fetched the menu's counts on every query-param change on /loads (red-team R11, 2026-10-16).
          if (this.active && !wasActive) this.announceOpenGroup();
        } else {
          if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
            this.active = false;
          }
        }
      });
    });

    this.menuResetSubscription = this.menuService.resetSource$.subscribe(() => {
      this.active = false;
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.updateLinkQueryParams();
      if ((this.isSlimPlus || this.isSlim) && !this.isAccordion) {
        this.active = false;
      } else {
        if (this.item.routerLink) {
          this.updateActiveStateFromRoute();
        }
      }
    });
  }

  ngOnInit() {
    this.key = this.parentKey ? this.parentKey + '-' + this.index : String(this.index);
    this.updateLinkQueryParams();
    if (this.item.overviewLabel && this.item.routerLink) {
      // An icon only where its siblings carry one, so the labels in the list line up either way.
      this.overviewItem = {
        label: this.item.overviewLabel,
        icon: this.item.items?.some((child) => child.icon) ? 'pi pi-list' : undefined,
        routerLink: this.item.routerLink,
        queryParams: this.item.queryParams
      };
    }

    // Excludes slim-plus as well as slim, because in BOTH of those modes a submenu is a hover
    // fly-out rather than an inline tree. Marking the route-matching item active there does not
    // "expand the current section" — it opens a floating panel over the page, unasked. On sign-in
    // the app lands on /loads, which is Search Loads' own routerLink, so its seven-item fly-out
    // appeared by itself every time.
    //
    // The guard named only `isSlim` because slim-plus had never actually run: menuMode was 'slim'
    // until US-3327 switched it. The NavigationEnd handler above already treats the two modes as a
    // pair (`isSlimPlus || isSlim` → close), and this is the one place that did not.
    //
    // Nothing else auto-opens a fly-out: `routeEvent` is emitted only from
    // updateActiveStateFromRoute, so blocking it here blocks the whole chain. The active-route
    // highlight is unaffected — that comes from routerLinkActive on the anchor.
    // The wide desktop menu is an inline list, not a fly-out, so there the current page's group opens on load.
    if ((this.isAccordion || (!this.isSlim && !this.isSlimPlus)) && this.item.routerLink) {
      this.updateActiveStateFromRoute();
    }
  }

  ngAfterViewChecked() {
    if (this.root && this.active && !this.isAccordion && this.layoutService.isDesktop() && (this.layoutService.isSlim() || this.layoutService.isSlimPlus())) {
      this.calculatePosition(this.submenu()?.nativeElement, this.submenu()?.nativeElement.parentElement);
    }
  }

  calculatePosition(overlay: HTMLElement, target: HTMLElement) {
    if (overlay) {
      const { top } = target.getBoundingClientRect();
      const vHeight = window.innerHeight;
      const oHeight = overlay.offsetHeight;
      // reset
      overlay.style.top = '';
      overlay.style.left = '';

      if (this.layoutService.isSlim() || this.layoutService.isSlimPlus()) {
        // Measured from the box the pop-out is positioned in (the menu), not from a top bar: on desktop the
        // menu now starts at the top of the screen, so subtracting the top bar put every pop-out ~70px off
        // the item under the mouse. The few px up line its title up with the hovered item's label.
        const parentTop = (overlay.offsetParent as HTMLElement | null)?.getBoundingClientRect().top ?? 0;
        const topOffset = top - parentTop - 6;
        const overflow = parentTop + topOffset + oHeight - vHeight;
        overlay.style.top = `${overflow > 0 ? topOffset - overflow : topOffset}px`;
      }
    }
  }

  private updateLinkQueryParams() {
    const own = this.item?.queryParams ?? null;
    this.linkQueryParams = isEmbeddedView() ? { ...own, view: 'embedded' } : own;
  }

  updateActiveStateFromRoute() {
    if (!this.item.routerLink) return;
    let activeRoute = this.router.isActive(this.item.routerLink[0], {
      paths: 'exact',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored'
    });

    if (activeRoute) {
      this.menuService.onMenuStateChange({ key: this.key, routeEvent: true });
    }
  }

  itemClick(event: MouseEvent, item: MenuItemDto) {
    // A modified click opens the link in a browser tab; this window and its open load tab stay as they are (F-019).
    if (!this.item.items && (event.ctrlKey || event.metaKey || event.shiftKey)) return;
    if (item) {
      this.menuService.addNavigation(item);
    }
    // navigate with hover
    if (((this.root && this.isSlim) || this.isSlimPlus) && !this.isAccordion) {
      this.layoutService.state.menuHoverActive = !this.layoutService.state.menuHoverActive;
    }

    // toggle active state
    if (this.item.items) {
      this.active = !this.active;
      if (this.active) this.announceOpenGroup();
      if (this.root && this.active && (this.isSlim || this.isSlimPlus) && !this.isAccordion) {
        this.layoutService.onOverlaySubmenuOpen();
      }
    } else {
      if (this.layoutService.isMobile()) {
        this.layoutService.state.staticMenuMobileActive = false;
      }

      if ((this.isSlim || this.isSlimPlus) && !this.isAccordion) {
        this.menuService.reset();
        this.layoutService.state.menuHoverActive = false;
      }
    }

    this.menuService.onMenuStateChange({ key: this.key });
  }

  @HostListener('mouseenter')
  onMenuItemEnter() {
    // The wide menu opens groups on click, but still tells the menu a group is under the mouse: that is what
    // loads Search Loads' live counts ("Intransit (4)").
    if (this.item.items && this.isAccordion) {
      this.mouseEnterItemEvent.emit(this.item);
      return;
    }
    if (this.item.items && (this.isSlim || this.isSlimPlus) && !this.isAccordion) {
      clearTimeout(this.hoverTimeoutId);
      this.menuService.onMenuStateChange({ key: this.key });
      this.active = true;
      this.mouseEnterItemEvent.emit(this.item);
    }
  }

  @HostListener('mouseleave')
  onMenuItemLeave() {
    if (this.item.items && this.isAccordion) {
      this.mouseEnterItemEvent.emit(null);
      return;
    }
    if (this.item.items && (this.isSlim || this.isSlimPlus) && !this.isAccordion) {
      this.hoverTimeoutId = setTimeout(() => {
        this.active = false;
        this.mouseEnterItemEvent.emit(null);
      }, 200);
    }
  }

  isCollapsed() {
    return !this.root && !this.active && this.layoutService.isDesktop() && !this.layoutService.isSlim() && !this.layoutService.isSlimPlus();
  }

  isExpanded() {
    return (this.root || this.active) && this.layoutService.isDesktop() && !this.layoutService.isSlim() && !this.layoutService.isSlimPlus();
  }

  isHidden() {
    return this.layoutService.isDesktop() && (this.layoutService.isSlim() || this.layoutService.isSlimPlus()) && !this.active;
  }

  isVisible() {
    return this.layoutService.isDesktop() && (this.layoutService.isSlim() || this.layoutService.isSlimPlus()) && this.active;
  }

  get isSlim() {
    return this.layoutService.isSlim();
  }

  get isSlimPlus() {
    return this.layoutService.isSlimPlus();
  }

  /**
   * The wide desktop menu (premium-shell.css `.layout-wide`, Dan 2026-10-16): a group's sub-pages open inline
   * under it on click, one group at a time, instead of in a hover pop-out the mouse had to travel to.
   */
  get isAccordion() {
    // Collapsed to the icon rail, groups go back to fly-outs: there is no room to open them in place.
    return this.layoutService.isSlimPlus() && this.layoutService.isDesktop() && !this.userPreferences.menuCollapsed();
  }

  /** A group row in the wide menu: it opens and closes its list and does not navigate. */
  /**
   * A wide-menu group that just opened tells the menu, which loads Search Loads' live counts ("Intransit (4)") at that
   * moment, not only when the mouse passes over it. Enter then leave, so the menu's "one load per hover" gate resets.
   */
  private announceOpenGroup(): void {
    if (this.isAccordion && this.root && this.item.items?.length) {
      this.mouseEnterItemEvent.emit(this.item);
      this.mouseEnterItemEvent.emit(null);
    }
  }

  /**
   * The wide menu marks the group that holds the page you are on, even while the group is closed, so the menu always
   * shows where you are (UI review, 2026-10-16).
   */
  @HostBinding('class.current-section')
  get isCurrentSection(): boolean {
    if (!this.isAccordion || !this.root || !this.item.items?.length) return false;
    // By section (the address's first segment), so a record page counts too: /organizations/company/{id} is in
    // Organizations even though no menu entry points at it.
    const section = (url: string) => url.split(/[?#]/)[0].split('/').filter(Boolean)[0] ?? '';
    const current = section(this.router.url);
    if (!current) return false;
    return [this.item, ...this.item.items]
      .filter((i) => i.routerLink)
      .some((i) => section(this.router.serializeUrl(this.router.createUrlTree(i.routerLink!))) === current);
  }

  get isGroupToggle() {
    return this.isAccordion && !!this.item.items?.length;
  }

  get isMobile() {
    return this.layoutService.isMobile();
  }

  @HostBinding('class.active-menuitem')
  get activeClass() {
    return this.active && !this.root;
  }

  ngOnDestroy() {
    if (this.menuSourceSubscription) {
      this.menuSourceSubscription.unsubscribe();
    }

    if (this.menuResetSubscription) {
      this.menuResetSubscription.unsubscribe();
    }
  }
}
